import axios from 'axios';
import { supabase } from '@/core/supabase';
import { analysisApiRuntime } from '@/config/featureFlags';
import { mockHandler } from '../mocks';

// 当前mock作用域：'all' 或 'ai-job-only'
let currentMockScope = analysisApiRuntime.mockScope;

/**
 * 检查请求路径是否应该使用mock（基于mockScope配置）
 * @param {string} path - 请求路径
 * @param {string} scope - mock作用域
 * @returns {boolean}
 */
const shouldUseMockForPath = (path, scope = currentMockScope) => {
    if (scope === 'all') {
        return true;
    }
    // ai-job-only模式：只有ai-jobs相关接口走mock
    if (scope === 'ai-job-only') {
        return path?.includes('/ai-jobs');
    }
    return true;
};

/**
 * 设置mock作用域
 * @param {string} scope - 'all' 或 'ai-job-only'
 */
export const setAnalysisMockScope = (scope) => {
    const validScopes = ['all', 'ai-job-only'];
    if (validScopes.includes(scope)) {
        currentMockScope = scope;
        console.log('[AnalysisApiClient] Mock scope set to:', scope);
    } else {
        console.warn('[AnalysisApiClient] Invalid mock scope:', scope);
    }
};

/**
 * 获取当前mock作用域
 * @returns {string}
 */
export const getAnalysisMockScope = () => currentMockScope;

// 统一消费 runtime config，避免业务模块直接维护本地 env 默认值。
if (analysisApiRuntime.mockEnabled && !mockHandler.isActive()) {
    mockHandler.setActive(true);
    mockHandler.useScenarios(analysisApiRuntime.mockScenarios);
    console.log('[AnalysisApiClient] Mock enabled from runtime config', {
        scenarios: analysisApiRuntime.mockScenarios
    });
}

export const ANALYSIS_API_BASE_URL = analysisApiRuntime.baseURL;
const MOCK_HTTP_META_KEY = '__mockHttpMeta';

// 分析链路请求默认超时。axios 默认 0 = 永不超时，弱网断连时 resolveRunAssets /
// authorization / createRun 会永远 pending，execution_started 之后再无任何事件
// （生产案例：execution_started 后静默挂死、runs 永不提交）。显式声明，双层保险
// （apiService 侧另有 axios.defaults.timeout = 30000，此处不依赖其 import 顺序）。
export const ANALYSIS_API_TIMEOUT_MS = 30000;

const isObjectRecord = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const buildNormalizedError = (error, requestContext = {}) => {
    const responseData = error?.response?.data || null;
    const normalizedError = new Error(
        responseData?.detail?.message ||
        responseData?.message ||
        responseData?.error ||
        responseData?.status ||
        error?.message ||
        'analysis_api_request_failed'
    );

    normalizedError.code = responseData?.detail?.reason_code
        || responseData?.reason_code
        || responseData?.code
        || responseData?.error
        || responseData?.status
        || error?.code
        || 'analysis_api_request_failed';
    normalizedError.status = error?.response?.status || responseData?.status_code || null;
    normalizedError.responseData = responseData;
    normalizedError.requestContext = requestContext;
    return normalizedError;
};

const buildNormalizedResponseError = (response, requestContext = {}) => {
    const responseData = response?.data || null;
    const normalizedError = new Error(
        responseData?.detail?.message ||
        responseData?.message ||
        responseData?.error ||
        responseData?.status ||
        'analysis_api_request_failed'
    );

    normalizedError.code = responseData?.detail?.reason_code
        || responseData?.reason_code
        || responseData?.code
        || responseData?.error
        || responseData?.status
        || 'analysis_api_request_failed';
    normalizedError.status = response?.status || responseData?.status_code || null;
    normalizedError.responseData = responseData;
    normalizedError.requestContext = requestContext;
    return normalizedError;
};

const normalizeMockSuccessResponse = (response) => {
    if (
        isObjectRecord(response) &&
        isObjectRecord(response[MOCK_HTTP_META_KEY]) &&
        Object.prototype.hasOwnProperty.call(response, 'data')
    ) {
        const mockMeta = response[MOCK_HTTP_META_KEY];
        return {
            data: response.data ?? null,
            status: Number(mockMeta.status) || 200,
            headers: isObjectRecord(mockMeta.headers) ? mockMeta.headers : {}
        };
    }

    return {
        data: response ?? null,
        status: 200,
        headers: {}
    };
};

export class AnalysisApiClient {
    constructor({ baseURL = ANALYSIS_API_BASE_URL } = {}) {
        this.baseURL = baseURL;
    }

    async getAuthHeaders() {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
            return {};
        }

        return {
            Authorization: `Bearer ${session.access_token}`
        };
    }

    async request({
        method = 'GET',
        path,
        data,
        params,
        headers = {},
        validateStatus,
        includeMeta = false,
        timeout = ANALYSIS_API_TIMEOUT_MS
    }) {
        const requestPath = String(path || '').trim();
        if (!requestPath) {
            throw new Error('[AnalysisApiClient] path is required');
        }

        // Mock模式优先判断（但需通过scope检查）
        if (mockHandler.isActive() && shouldUseMockForPath(requestPath)) {
            try {
                const mockResponse = await mockHandler.handle({
                    method,
                    path: requestPath,
                    data,
                    params,
                    includeMeta
                });
                const normalizedMockResponse = normalizeMockSuccessResponse(mockResponse);
                console.log('[AnalysisApiClient] Mock response:', { method, path: requestPath, scope: currentMockScope });
                if (includeMeta) {
                    return normalizedMockResponse;
                }
                return normalizedMockResponse.data;
            } catch (mockError) {
                // mock错误需要标准化后抛出
                if (mockError.isMockError || mockError.code?.startsWith('mock_')) {
                    const normalizedMockError = new Error(mockError.message);
                    normalizedMockError.code = mockError.code;
                    normalizedMockError.status = mockError.status;
                    normalizedMockError.isMockError = true;
                    normalizedMockError.responseData = mockError.responseData || null;
                    throw normalizedMockError;
                }
                // 非mock错误继续抛给真实请求处理
                throw mockError;
            }
        }

        // scope=ai-job-only时，非ai-jobs请求会走到这里
        if (mockHandler.isActive() && currentMockScope === 'ai-job-only') {
            console.log('[AnalysisApiClient] Bypassing mock for non-ai-jobs request:', { method, path: requestPath });
        }

        try {
            const authHeaders = await this.getAuthHeaders();
            const response = await axios({
                method,
                url: `${this.baseURL}${requestPath}`,
                data,
                params,
                headers: {
                    ...authHeaders,
                    ...headers,
                    'Content-Type': 'application/json'
                },
                validateStatus,
                timeout
            });
            if (Number(response?.status) >= 400) {
                throw buildNormalizedResponseError(response, {
                    method,
                    path: requestPath,
                    params: params || null
                });
            }

            if (includeMeta) {
                return {
                    data: response?.data ?? null,
                    status: Number(response?.status) || null,
                    headers: response?.headers || {}
                };
            }

            return response.data;
        } catch (error) {
            if (error?.responseData || error?.requestContext) {
                throw error;
            }
            throw buildNormalizedError(error, {
                method,
                path: requestPath,
                params: params || null
            });
        }
    }

    async get(path, options = {}) {
        return this.request({
            method: 'GET',
            path,
            ...options
        });
    }

    async post(path, data, options = {}) {
        return this.request({
            method: 'POST',
            path,
            data,
            ...options
        });
    }

    /**
     * 检查当前是否启用了 mock 模式
     */
    isMockEnabled() {
        return mockHandler.isActive();
    }
}

export const analysisApiClient = new AnalysisApiClient();

export default analysisApiClient;

/**
 * Mock控制API（开发调试用）
 * 使用示例：
 *   import { enableAnalysisMock, disableAnalysisMock, setAnalysisMockScope } from '@/core/analysis/api/AnalysisApiClient'
 *   enableAnalysisMock()  // 启用mock
 *   disableAnalysisMock() // 禁用mock
 *   setAnalysisMockScope('ai-job-only') // 仅ai-jobs接口走mock
 *   setAnalysisMockScope('all')         // 所有analysis接口走mock
 */
export const enableAnalysisMock = () => {
    mockHandler.setActive(true);
    mockHandler.useScenarios(analysisApiRuntime.mockScenarios);
    console.log('[AnalysisApiClient] Mock enabled, scope:', currentMockScope);
};

export const disableAnalysisMock = () => {
    mockHandler.setActive(false);
    console.log('[AnalysisApiClient] Mock disabled');
};

export const isAnalysisMockEnabled = () => mockHandler.isActive();

// 重新导出mockHandler供高级用法
export { mockHandler };
